import {
  GA_LABEL2,
  GA_LABEL3,
  GA_SCREEN_NAME,
  SELECT_BY_WISHLINK_CATEGORY_RANKING,
} from "../../utils/constants";

export const getCustomParam = (queryParams) => {
  const entrypoint = queryParams.get("entrypoint");
  if (entrypoint === "COLLECTION") return GA_LABEL3.RECOMMENDED_COLLECTION;
  else if (entrypoint === "BANNER") return GA_LABEL3.CUSTOM_COLLECTION;
  else return "";
};

export const getLabel3 = (queryParams) => {
  const entrypoint = queryParams.get("entrypoint");
  if (entrypoint === "COLLECTION") return GA_LABEL3.RECOMMENDED_COLLECTION;
  else if (entrypoint === "BANNER") return GA_LABEL3.CUSTOM_COLLECTION;
  else return "";
};

export const JOURNEY_PARAM = "journey";
export const TREND_SECTION_PARAM = "section";
export const JOURNEY_CATEGORIES_PARAM = "cat";

export const SOURCING_REAL_ESTATE = {
  CATEGORY_WIDGET: "category_widget",
  TLP: "tlp",
  PLP_TREND_SECTION: "plp_trend_section",
  PDP_TREND_SECTION: "pdp_trend_section",
  SIMILAR_PRODUCTS: "similar_products",
};

export const TREND_SECTION = {
  HERO: "hero",
  PLP: "plp",
  PLP_TREND_SECTION: "plp_trend_section",
};

export const selectedCategoryNames = (filterOptionsSelected, queryParams) =>
  (filterOptionsSelected?.[3]?.options ?? [])
    .map(
      (v) =>
        SELECT_BY_WISHLINK_CATEGORY_RANKING.find((c) => c.value === v)?.label ??
        String(v)
    )
    .join(", ") ||
  (queryParams?.get(JOURNEY_CATEGORIES_PARAM) ?? "");

/** Forward the category context onto a URLSearchParams when leaving a listing. */
export const setJourneyCategoriesParam = (searchParams, categoryNames) => {
  if (categoryNames) searchParams.set(JOURNEY_CATEGORIES_PARAM, categoryNames);
  else searchParams.delete(JOURNEY_CATEGORIES_PARAM);
  return searchParams;
};

/** `{section, name, rank}` — the trend label every trend event carries. */
export const trendGaLabel = (trend, section = "") =>
  JSON.stringify({
    section: section || "",
    name: trend?.trendName ?? trend?.name ?? "",
    rank: trend?.rank == null ? "" : String(trend.rank),
  });

export const readJourney = (queryParams) =>
  (queryParams?.get(JOURNEY_PARAM) || "")
    .split(",")
    .filter((s) => s && s !== SOURCING_REAL_ESTATE.CATEGORY_WIDGET);

/** Write the journey (minus the derived category prefix) onto a URLSearchParams. */
export const setJourneyParam = (searchParams, stack = []) => {
  const path = (stack || []).filter(
    (s) => s && s !== SOURCING_REAL_ESTATE.CATEGORY_WIDGET
  );
  if (path.length) searchParams.set(JOURNEY_PARAM, path.join(","));
  else searchParams.delete(JOURNEY_PARAM);
  return searchParams;
};

/**
 * GA params for one event on the Seleqt sourcing journey.
 *  - `inside`: real estates the event fires inside of, on top of what the URL carries
 *    (e.g. ["tlp"] on the trend landing page, ["similar_products"] in that rail).
 *  - `trend` / `trendSection`: the trend the journey is about, when any.
 * Returns real_estate1..3 plus label/label2 and the resolved `journey` stack.
 */
export const sourcingJourneyGa = ({
  queryParams,
  filterOptionsSelected,
  inside = [],
  trend = null,
  trendSection = "",
} = {}) => {
  const categoryNames = selectedCategoryNames(
    filterOptionsSelected,
    queryParams
  );
  const journey = [
    ...(categoryNames ? [SOURCING_REAL_ESTATE.CATEGORY_WIDGET] : []),
    ...readJourney(queryParams),
    ...inside.filter(Boolean),
  ].slice(0, 3);
  const labels = [];
  if (categoryNames) labels.push(categoryNames);
  if (trend) labels.push(trendGaLabel(trend, trendSection));
  if (journey.includes(SOURCING_REAL_ESTATE.SIMILAR_PRODUCTS))
    labels.push(SOURCING_REAL_ESTATE.SIMILAR_PRODUCTS);
  return {
    realEstate1: journey[0] ?? "",
    realEstate2: journey[1] ?? "",
    realEstate3: journey[2] ?? "",
    label: labels[0] ?? "",
    label2: labels[1] ?? "",
    journey,
    categoryNames,
  };
};

/** SBWL Collections PLP: event_label=collection vs category_widget; label2=list of selected category widget labels OR commission/fallback string. */
export const getSbwlCollectionPlListingLabels = (
  filterOptionsSelected,
  collectionListingLabel2Fallback = "",
  queryParams,
  product
) => {
  const plpCollectionId = queryParams?.get("plpCollectionId");
  const catOpts = filterOptionsSelected?.[3]?.options ?? [];
  if (catOpts.length > 0) {
    const label2 = catOpts
      .map(
        (v) =>
          SELECT_BY_WISHLINK_CATEGORY_RANKING.find((c) => c.value === v)
            ?.label ?? String(v)
      )
      .join(", ");
    return { label: "category_widget", label2 };
  }
  if (plpCollectionId) {
    return { label: "collection", label2: collectionListingLabel2Fallback };
  }
  if (product) {
    return { label: "plp_main", label2: collectionListingLabel2Fallback };
  }
  return {
    label: "pdp_similar_products",
    label2: collectionListingLabel2Fallback,
  };
};

/** Payload for PRODUCT_IMPRESSION / ProductCard-style click on SBWL grids (price commissioned into label3). */
export const buildSbwlProductListingGaDimensions = ({
  filterOptionsSelected,
  commissionStr,
  product,
  queryParams,
  getLabel3Fn = getLabel3,
  inside = [],
  trend = null,
  trendSection = "",
}) => {
  const price = product?.price ?? "";
  const invOrEntry =
    product?.total_inventory > 0
      ? product.total_inventory
      : getLabel3Fn(queryParams);
  const journey = sourcingJourneyGa({
    queryParams,
    filterOptionsSelected,
    inside,
    trend,
    trendSection,
  });
  if (journey.realEstate1) {
    return {
      realEstate1: journey.realEstate1,
      realEstate2: journey.realEstate2,
      realEstate3: journey.realEstate3,
      label: journey.label,
      label2: journey.label2 || commissionStr || "",
      label3: `${price}|${invOrEntry}`,
    };
  }
  const listing = getSbwlCollectionPlListingLabels(
    filterOptionsSelected,
    commissionStr ?? "",
    queryParams,
    product
  );
  return {
    label: listing.label,
    label2: commissionStr ?? "",
    label3: `${price}|${invOrEntry}`,
  };
};

/** PDP add-to-cart label2: category_widget | collection | pdp_similar_products (PLP listing source). */
export const getPdpAddToCartLabel2 = ({
  queryParams,
  filterOptionsSelected,
  collectionId,
}) => {
  const fromNav = queryParams?.get("gaLabel2");
  if (fromNav) return fromNav;

  const catOpts = filterOptionsSelected?.[3]?.options ?? [];
  if (catOpts.length > 0) return "category_widget";

  if (queryParams?.get("plpCollectionId") || collectionId) {
    return GA_LABEL2.COLLECTION;
  }

  return "pdp_similar_products";
};

export const getSubCategory = (queryParams) => {
  const subcategoryId = queryParams.get("subcategoryId");
  if (subcategoryId) {
    return subcategoryId;
  }
  return "";
};

export const determinePLPSaveForLater = (queryParams) => {
  const entrypoint = queryParams.get("entrypoint");
  if (entrypoint === "CATEGORY") return GA_SCREEN_NAME.CATEGORY_PLP;
  else if (entrypoint === "BANNER" || entrypoint === "COLLECTION")
    return GA_SCREEN_NAME.COLLECTION_PLP;
  else if (entrypoint === "BRAND") return GA_SCREEN_NAME.BRAND_PLP;
  else if (entrypoint === "SEARCH") return GA_SCREEN_NAME.SEARCH_PLP;
  else return GA_SCREEN_NAME.SAVE_FOR_LATER_PLP;
};

export const determinePLPSourcing = (queryParams) => {
  const entrypoint = queryParams.get("entrypoint");
  if (entrypoint === "CATEGORY") return GA_SCREEN_NAME.CATEGORY_PLP;
  else if (entrypoint === "BANNER" || entrypoint === "COLLECTION")
    return GA_SCREEN_NAME.COLLECTION_PLP;
  else if (entrypoint === "BRAND") return GA_SCREEN_NAME.BRAND_PLP;
  else if (entrypoint === "SEARCH") return GA_SCREEN_NAME.SEARCH_PLP;
  else return GA_SCREEN_NAME.SOURCING;
};

export const determinePLPEmpty = (queryParams) => {
  const entrypoint = queryParams.get("entrypoint");
  if (entrypoint === "CATEGORY") return GA_SCREEN_NAME.CATEGORY_PLP;
  else if (entrypoint === "BANNER" || entrypoint === "COLLECTION")
    return GA_SCREEN_NAME.COLLECTION_PLP;
  else if (entrypoint === "BRAND") return GA_SCREEN_NAME.BRAND_PLP;
  else if (entrypoint === "SEARCH") return GA_SCREEN_NAME.SEARCH_PLP;
  else return "";
};

export const getVariantFromUrl = (location) => {
  const pathname = location?.pathname;
  if (pathname.startsWith("/brand-sourcing")) return "brand-sourcing";
  else return "sourcing";
};

export const getLabel = (searchParams, collectionDetails) => {
  const entrypoint = searchParams.get("entrypoint");
  if (entrypoint === "CATEGORY") return collectionDetails?.display_name;
  else if (entrypoint === "BRAND") return collectionDetails?.name;
  else if (entrypoint === "COLLECTION" || entrypoint === "BANNER")
    return collectionDetails?.title;
  else return "";
};
